import { circle } from '@turf/circle'
import { distance } from '@turf/distance'
import { featureCollection, point, lineString } from '@turf/helpers'
import type { Geometry } from 'geojson'
import { throttle } from 'lodash'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Layer, type MapLayerMouseEvent, Source, useMap } from 'react-map-gl/maplibre'
import { useTheme } from '@mui/material/styles'
import { type KNMapLibreCircleSelectorProps, type MapCircleSelection, MapSelectionMode } from './types'

const MIN_RADIUS = 25

const KNMapLibreCircleSelector = ({
  mode,
  selections = [],
  onChange,
}: KNMapLibreCircleSelectorProps): ReactNode => {
  const theme = useTheme()
  const { current: map } = useMap()
  const [radiusFeatureCollection, setRadiusFeatureCollection] = useState(featureCollection([]))
  const [drawingCenter, setDrawingCenter] = useState<[number, number] | null>(null)
  const [drawingFeatureCollection, setDrawingFeatureCollection] = useState(featureCollection([]))

  const updateSelectionState = useCallback(() => {
    const radiusPolygons = selections.filter((selection) => selection.mode === MapSelectionMode.CIRCLE)
      .map((selection) => circle(
        (selection as MapCircleSelection).center,
        (selection as MapCircleSelection).radius,
        { steps: 64, units: 'kilometers', properties: { color: selection.color } },
      ))
    setRadiusFeatureCollection(featureCollection(radiusPolygons))
  }, [selections])

  useEffect(() => {
    updateSelectionState()
  }, [selections])

  useEffect(() => {
    if (!map) {
      return
    }

    map.on('load', updateSelectionState)
    map.on('mousemove', handleMouseMoveThrottled)
    map.on('click', handleClick)

    return () => {
      map.off('load', updateSelectionState)
      map.off('mousemove', handleMouseMoveThrottled)
      map.off('click', handleClick)
    }
  }, [map, mode, selections, updateSelectionState, drawingCenter])

  useEffect(() => {
    if (mode === undefined) {
      setDrawingCenter(null)
      setDrawingFeatureCollection(featureCollection([]))
    }
  }, [mode])

  const handleMouseMove = useCallback(async (event: MapLayerMouseEvent) => {
    if (mode !== MapSelectionMode.CIRCLE) {
      return
    }
    if (!drawingCenter) {
      event.target.getCanvas().style.cursor = 'pointer'
      return
    }
    const radius = Math.max(MIN_RADIUS, distance(drawingCenter, [event.lngLat.lng, event.lngLat.lat]))
    const radiusLine = lineString([drawingCenter, [event.lngLat.lng, event.lngLat.lat]])
    const circlePolygon = circle(drawingCenter, radius, { steps: 64, units: 'kilometers' })
    // use Geometry type for the feature collection to safely include both LineString and Polygon features
    setDrawingFeatureCollection(featureCollection<Geometry>([radiusLine, circlePolygon]))
  }, [mode, drawingCenter])
  const handleMouseMoveThrottled = useMemo(
    () => throttle(handleMouseMove, 16, { leading: true, trailing: false }),
    [handleMouseMove],
  )

  const handleClick = useCallback(async (event: MapLayerMouseEvent) => {
    if (mode !== MapSelectionMode.CIRCLE) {
      return
    }
    if (drawingCenter) {
      onChange({
        mode: MapSelectionMode.CIRCLE,
        center: drawingCenter,
        radius: distance(point(drawingCenter), point([event.lngLat.lng, event.lngLat.lat])),
      } as MapCircleSelection)
      event.target.getCanvas().style.cursor = ''
    } else {
      setDrawingCenter([event.lngLat.lng, event.lngLat.lat])
      const circlePolygon = circle([event.lngLat.lng, event.lngLat.lat], MIN_RADIUS, { steps: 64, units: 'kilometers' })
      setDrawingFeatureCollection(featureCollection([circlePolygon]))
      event.target.getCanvas().style.cursor = 'crosshair'
    }
  }, [mode, drawingCenter, onChange])

  return (
    <>
      <Source
        id="selector_radius_drawing"
        type="geojson"
        data={drawingFeatureCollection}
      >
        <Layer
          id="selector_radius_drawing_outline"
          type="line"
          source="selector_radius_drawing"
          beforeId="label-other"
          paint={{
            'line-width': 2,
            'line-color': theme.palette.secondary.main,
            'line-dasharray': [1, 1],
          }}
        />
      </Source>

      <Source
        id="selector_radius"
        type="geojson"
        data={radiusFeatureCollection}
      >
        <Layer
          id="selector_radius_fill"
          type="fill"
          source="selector_radius"
          beforeId="label-other"
          paint={{
            'fill-color': [
              'coalesce',
              ['get', 'color'],
              theme.palette.primary.main,
            ],
            'fill-opacity': 0.5,
          }}
        />
      </Source>
    </>
  )
}

export default KNMapLibreCircleSelector
